import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { getUsers, getConversation, sendMessage } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Search, Send, MessageSquare, ArrowLeft, Users, Hash } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Messages() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [channelMessages, setChannelMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const autoOpenDone = useRef(false);

  useEffect(() => { fetchUsers(); fetchChannels(); }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchConversation(selectedUser.id);
      const interval = setInterval(() => fetchConversation(selectedUser.id), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedUser]);

  useEffect(() => {
    if (selectedChannel) {
      fetchChannelMessages(selectedChannel.id);
      const interval = setInterval(() => fetchChannelMessages(selectedChannel.id), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedChannel]);

  useEffect(() => { scrollToBottom(); }, [messages, channelMessages]);

  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); };

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      const otherUsers = res.data.filter(u => u.id !== user?.id);
      setUsers(otherUsers);

      // Auto-open conversation from ?user= query param
      const targetUserId = searchParams.get('user');
      if (targetUserId && !autoOpenDone.current) {
        autoOpenDone.current = true;
        const target = otherUsers.find(u => u.id === targetUserId);
        if (target) {
          setSelectedUser(target);
          setSearchParams({}, { replace: true });
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchChannels = async () => {
    try {
      const res = await axios.get(`${API}/channels`);
      setChannels(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchConversation = async (userId) => {
    try {
      const res = await getConversation(userId);
      setMessages(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchChannelMessages = async (channelId) => {
    try {
      const res = await axios.get(`${API}/channels/${channelId}/messages`);
      setChannelMessages(res.data);
    } catch (err) { console.error(err); }
  };

  const handleSendDM = async () => {
    if (!newMessage.trim() || !selectedUser) return;
    setSending(true);
    try {
      await sendMessage({ receiver_id: selectedUser.id, content: newMessage });
      setNewMessage('');
      fetchConversation(selectedUser.id);
    } catch (err) { toast.error('Error al enviar'); }
    finally { setSending(false); }
  };

  const handleSendChannelMsg = async () => {
    if (!newMessage.trim() || !selectedChannel) return;
    setSending(true);
    try {
      await axios.post(`${API}/channels/${selectedChannel.id}/messages?content=${encodeURIComponent(newMessage)}`);
      setNewMessage('');
      fetchChannelMessages(selectedChannel.id);
    } catch (err) { toast.error('Error al enviar'); }
    finally { setSending(false); }
  };

  const handleCreateChannel = async () => {
    const name = prompt('Nombre del canal:');
    if (!name) return;
    try {
      await axios.post(`${API}/channels?name=${encodeURIComponent(name)}&is_public=true`);
      toast.success('Canal creado');
      fetchChannels();
    } catch (err) { toast.error('Error al crear canal'); }
  };

  const filteredUsers = users.filter(u =>
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  // Active conversation view
  if (selectedUser || selectedChannel) {
    const isChannel = !!selectedChannel;
    const activeMessages = isChannel ? channelMessages : messages;
    const title = isChannel ? `#${selectedChannel.name}` : selectedUser?.full_name;

    return (
      <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="chat-view">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b bg-white">
          <Button variant="ghost" size="icon" onClick={() => { setSelectedUser(null); setSelectedChannel(null); setNewMessage(''); }}
            data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          {!isChannel && (
            <Avatar className="w-9 h-9">
              <AvatarImage src={selectedUser?.avatar_url} />
              <AvatarFallback className="bg-stone-200 text-sm">{selectedUser?.full_name?.[0]}</AvatarFallback>
            </Avatar>
          )}
          {isChannel && <Hash className="w-5 h-5 text-stone-500" />}
          <div>
            <p className="font-medium text-stone-800">{title}</p>
            <p className="text-xs text-stone-400">{isChannel ? 'Canal grupal' : selectedUser?.email}</p>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-3">
            {activeMessages.map((msg, i) => {
              const isMine = msg.sender_id === user?.id;
              return (
                <div key={msg.id || i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] ${isMine ? 'order-2' : ''}`}>
                    {isChannel && !isMine && (
                      <p className="text-xs text-stone-400 mb-0.5 ml-1">{msg.sender_name}</p>
                    )}
                    <div className={`px-3 py-2 rounded-2xl text-sm ${
                      isMine ? 'bg-blue-600 text-white rounded-br-md' : 'bg-stone-100 text-stone-800 rounded-bl-md'
                    }`}>
                      {msg.content}
                    </div>
                    <p className={`text-[10px] mt-0.5 ${isMine ? 'text-right' : ''} text-stone-400`}>
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
            {activeMessages.length === 0 && (
              <div className="text-center py-12 text-stone-400">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Sin mensajes aún. ¡Inicia la conversación!</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="p-3 border-t bg-white">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Escribe un mensaje..."
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (isChannel ? handleSendChannelMsg() : handleSendDM())}
              data-testid="message-input"
            />
            <Button onClick={isChannel ? handleSendChannelMsg : handleSendDM} disabled={sending || !newMessage.trim()}
              data-testid="send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Contact/Channel List
  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Mensajería</h1>
          <p className="text-stone-500 text-sm">{users.length} contactos</p>
        </div>
      </div>

      <Tabs defaultValue="dm">
        <TabsList>
          <TabsTrigger value="dm"><MessageSquare className="w-4 h-4 mr-1" />Directos</TabsTrigger>
          <TabsTrigger value="channels"><Hash className="w-4 h-4 mr-1" />Canales ({channels.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="dm">
          <Card>
            <CardHeader>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input placeholder="Buscar contacto..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="contacts-search" />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {filteredUsers.map(u => (
                  <button key={u.id} onClick={() => { setSelectedUser(u); setSelectedChannel(null); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors text-left"
                    data-testid={`contact-${u.id}`}>
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={u.avatar_url} />
                      <AvatarFallback className="bg-stone-200">{u.full_name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-800 truncate">{u.full_name}</p>
                      <p className="text-xs text-stone-400 truncate">{u.email}</p>
                    </div>
                  </button>
                ))}
              </div>
              {filteredUsers.length === 0 && (
                <p className="text-center py-8 text-stone-400">No se encontraron contactos</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="channels">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Canales Grupales</CardTitle>
              <Button size="sm" onClick={handleCreateChannel} data-testid="create-channel-btn">
                <Hash className="w-4 h-4 mr-1" /> Nuevo Canal
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {channels.map(ch => (
                  <button key={ch.id} onClick={() => { setSelectedChannel(ch); setSelectedUser(null); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors text-left"
                    data-testid={`channel-${ch.id}`}>
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Hash className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-800">#{ch.name}</p>
                      <p className="text-xs text-stone-400">{ch.is_public ? 'Canal público' : 'Canal privado'}</p>
                    </div>
                  </button>
                ))}
              </div>
              {channels.length === 0 && (
                <p className="text-center py-8 text-stone-400">No hay canales. Crea uno para empezar.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
